/**
 * HubSpot File Upload API
 *
 * Endpoint: POST /api/upload.php
 * Content-Type: multipart/form-data
 * Authorization: Bearer <token>
 *
 * Accepted types : PNG, JPG, JPEG, WebP
 * Max size       : 5 MB
 *
 * Response (success):
 * {
 *   success: true,
 *   file_info: {
 *     id: number,
 *     name: string,
 *     size: string,
 *     url: string,
 *     uploaded_at: string
 *   }
 * }
 *
 * Response (error):
 * {
 *   success: false,
 *   message: string
 * }
 */
import apiClient from './axiosInstance';

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB in bytes

export const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.webp';

/**
 * Validates a File object against size and type constraints.
 *
 * @param {File} file
 * @returns {{ valid: boolean; message?: string }}
 */
export const validateFile = (file) => {
  if (!file) return { valid: false, message: 'No file selected.' };

  if (!ACCEPTED_TYPES.includes(file.type)) {
    return { valid: false, message: 'Invalid file type. Accepted: PNG, JPG, JPEG, WebP.' };
  }

  return { valid: true };
};

/**
 * Uploads a file to the HubSpot files endpoint.
 *
 * @param {File} file
 * @returns {Promise<{ id: number; name: string; originalName: string; size: number; hsFileId: string; url: string; height: number; width: number }>}
 * @throws {Error} with `.message` safe to display in the UI
 */
export const uploadHsFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/api/upload.php', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const data = response.data;

  if (data.status !== 'success') {
    const err = new Error(data.message || 'Upload failed. Please try again.');
    err.status = response.status;
    throw err;
  }

  return data.file;
};
